import {
  getCurrentOwnerSub,
  isHeldOp,
  isLocalDraftScoreSheet,
  peekOutbox,
  scoreSheets,
  tombstones,
} from "./db";

// #1902 — the support-only "flush outbox and rebuild local data" recovery
// button's orchestration, kept apart from the component so it can be unit
// tested against the real outbox/db without rendering React.
//
// Order (AC3/AC4 of #1902):
//   1. Stop the engine's own periodic timers first, so nothing else touches
//      the outbox or the database while this runs (the periodic flush would
//      otherwise race the check below or the wipe itself).
//   2. Force one flush and wait for it to drain everything it can.
//   3. Re-peek the outbox. If ANYTHING is still queued (offline, a
//      backoff-scheduled retry, a held op from another sub, or a permanent
//      "terminal" failure) that is unsynced work — abort before the wipe,
//      delete nothing, tell the caller.
//   4. Card #3257 AC4: with the outbox empty, ALSO refuse while any local
//      scoreSheets row holds scores that never got an outbox op (see
//      findOpLessLocalSheets). Nothing in the outbox does not mean nothing
//      unsent: a pre-flight-blocked save writes the local row but enqueues
//      no op, and the wipe below would destroy the only copy.
//   5. Only then: wipe the database and re-hydrate.
//
// #2160: two failure surfaces the original lacked:
//   a. OFFLINE. flush() and hydrate() both early-return when offline, so an
//      offline tablet with an empty outbox read as a "clean flush", got
//      wiped, and downloaded nothing. The connectivity check runs BEFORE
//      anything is touched (and again after the flush, in case the link
//      dropped during it) and returns { ok: false, reason: "offline" } with
//      the database untouched.
//   b. HYDRATE THROWS after the wipe (listShows is the one unguarded call).
//      It is caught and returned as { ok: false, reason: "hydrateFailed" } so
//      the caller can tell the scribe their local copy was erased and not
//      refilled, instead of the rejection escaping the click handler.
//
// Card #3257 AC4 -- local score sheets with NO outbox op. Called only after the
// outbox has been confirmed empty, so every local draft row is by definition
// op-less. Same predicate the Queue page's Draft rows use (isLocalDraftScoreSheet:
// a provisional first create, or content diverged from its synced ancestor),
// minus entities with a tombstone: a deliberately discarded / sent-for-review
// sheet leaves a stale row behind on purpose, and wiping it loses nothing.
export async function findOpLessLocalSheets() {
  const drafts = [];
  for (const row of await scoreSheets.getAll()) {
    if (!isLocalDraftScoreSheet(row)) continue;
    // A create that lost the #2815 same-seat race keeps its provisional row on
    // purpose, stamped with contenderServerId (sync.js
    // markProvisionalSheetAsContender): the create already reached the server
    // (201, authoritative:false) and nothing will ever clear this row, so
    // counting it would make this station un-rebuildable forever.
    if (row?.provisional && row?.contenderServerId) continue;
    const entityKey = row?.rideId && row?.judgeId ? `${row.rideId}::${row.judgeId}` : null;
    if (entityKey && (await tombstones.get(entityKey))) continue;
    drafts.push(row);
  }
  return drafts;
}

// Card #3264 -- some op-less drafts can never be sent (an edit to an already
// shipped sheet that the #2810 pre-flight keeps blocking, a draft on another
// show's ride), so the #3257 refusal above would wedge Rebuild forever on that
// tablet. `sendSnapshotFirst` is the override: when the caller passes it (the
// dialog's "Rebuild anyway" step) the localDrafts refusal is replaced by
// "upload the station snapshot, and only if the office confirms receiving it,
// carry on and wipe". The snapshot is dumpDatabase() output, so the draft
// content is kept off-device before it is erased. A failed or unconfirmed
// upload returns { ok: false, reason: "snapshotFailed" } with nothing wiped.
// It overrides ONLY this refusal: the unsynced/held outbox refusal above is
// untouched (#3253/#3255), and offline still refuses first.
//
// `syncEngine` and `rebuildDb` are passed in (not imported as the app-wide
// singleton) so a test exercises this exact call site against a fake engine
// and a fake db, per #1902 AC5.
export async function flushAndRebuildLocalData({ syncEngine, rebuildDb, sendSnapshotFirst }) {
  if (!syncEngine.isOnline()) return { ok: false, reason: "offline" };
  syncEngine.stop();
  try {
    // Card #3255 -- ops another scribe's login queued in this org are the
    // show's scores, not that login's: send them under this session first, so
    // one foreign op cannot block the rebuild for everyone. Another org's ops
    // (and ops with no recorded org) stay held and are reported below.
    await syncEngine.claimForeignOps?.();
    await syncEngine.flush();
    const remaining = await peekOutbox();
    if (remaining.length > 0) {
      const sub = getCurrentOwnerSub();
      return {
        ok: false,
        reason: "unsynced",
        remaining: remaining.length,
        // How many of those are held behind a different login that this
        // session may not claim, so the dialog can say how to clear them.
        held: remaining.filter((op) => isHeldOp(op, sub)).length,
      };
    }
    const opLess = await findOpLessLocalSheets();
    if (opLess.length > 0) {
      if (!sendSnapshotFirst) {
        return { ok: false, reason: "localDrafts", remaining: opLess.length };
      }
      let sent = { ok: false };
      try {
        sent = await sendSnapshotFirst();
      } catch {
        // Treated as a failed upload below: never wipe on an unknown outcome.
      }
      if (!sent?.ok) {
        return { ok: false, reason: "snapshotFailed", remaining: opLess.length };
      }
    }
    // The link may have dropped during the flush: a wipe now would be
    // followed by a hydrate() that silently downloads nothing.
    if (!syncEngine.isOnline()) return { ok: false, reason: "offline" };
    await rebuildDb();
    // #3046 — hydrate() now refuses a no-signal ("implicit") run while the
    // engine is stopped, which this recovery deliberately is (stop() above).
    // Passing our OWN never-aborted controller signal marks this run as
    // caller-owned cancellation and lets the intentional post-wipe
    // re-hydrate through; its own abort state is the only gate.
    try {
      await syncEngine.hydrate(new AbortController().signal);
    } catch (error) {
      // The database is already wiped here; report it, never swallow it.
      return { ok: false, reason: "hydrateFailed", error };
    }
    // hydrate() exits quietly on its own !isOnline() check, so a link drop
    // during the wipe would otherwise read as success with an empty database.
    if (!syncEngine.isOnline()) return { ok: false, reason: "hydrateFailed" };
    return { ok: true };
  } finally {
    syncEngine.start();
  }
}
